import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Car, Pencil, Check, X, Users } from "lucide-react";

const emptyForm = { plate: "", brand: "", model: "", max_students: 2, active: true };

function VehicleForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || emptyForm);
  useEffect(() => { setForm(initial || emptyForm); }, [initial]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.plate.trim()) return;
    onSave({
      ...form,
      plate: form.plate.trim().toUpperCase(),
      brand: form.brand.trim(),
      model: form.model.trim(),
      max_students: Math.max(1, Math.min(8, parseInt(form.max_students || "2", 10))),
    });
  };

  return (
    <form
      onSubmit={submit}
      className="card-surface p-4 space-y-3"
      data-testid="vehicle-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Matrícula *</label>
          <input
            data-testid="vehicle-plate-input"
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value })}
            placeholder="1234-ABC"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Marca</label>
          <input
            data-testid="vehicle-brand-input"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            placeholder="Renault"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Modelo</label>
          <input
            data-testid="vehicle-model-input"
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder="Clio"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Capacidad máx.</label>
          <input
            data-testid="vehicle-capacity-input"
            type="number"
            min="1"
            max="8"
            value={form.max_students}
            onChange={(e) => setForm({ ...form, max_students: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="h-4 w-4 accent-orange-500"
            data-testid="vehicle-active"
          />
          Activo
        </label>
        <div className="flex gap-2">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" data-testid="vehicle-form-cancel">
              <X className="h-4 w-4" /> Cancelar
            </button>
          )}
          <button type="submit" disabled={saving} className="btn-primary" data-testid="vehicle-form-save">
            <Check className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function VehiclesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // vehicle being edited
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicles");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/vehicles/${editing.id}`, payload);
        toast.success("Vehículo actualizado");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehículo añadido");
      }
      setCreating(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`¿Eliminar el vehículo ${v.plate}?`)) return;
    try {
      // PIN-protected — the axios interceptor prompts the user automatically
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehículo eliminado");
      load();
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Vehículos</h2>
          <p className="text-xs text-neutral-500">
            Asigna un vehículo a cada cita para controlar la capacidad en sesiones compartidas.
          </p>
        </div>
        {!creating && !editing && (
          <button
            onClick={() => setCreating(true)}
            className="btn-primary"
            data-testid="vehicle-new-button"
          >
            <Plus className="h-4 w-4" /> Nuevo vehículo
          </button>
        )}
      </div>

      {(creating || editing) && (
        <VehicleForm
          initial={editing || emptyForm}
          onSave={save}
          onCancel={() => { setCreating(false); setEditing(null); }}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <Car className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
          <div className="text-sm text-neutral-500">
            Aún no hay vehículos. Añade el primero para poder asignarlos a las citas.
          </div>
        </div>
      ) : (
        <div className="card-surface divide-y divide-neutral-100" data-testid="vehicle-list">
          {items.map((v) => (
            <div
              key={v.id}
              data-testid={`vehicle-row-${v.id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                  <Car className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono font-bold text-sm">{v.plate}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {[v.brand, v.model].filter(Boolean).join(" · ") || "Sin datos"}
                    {!v.active && <span className="ml-2 text-red-500 font-semibold">Inactivo</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 bg-neutral-100 rounded-full px-2.5 py-1">
                  <Users className="h-3 w-3" /> Máx. {v.max_students}
                </span>
                <button
                  onClick={() => { setEditing(v); setCreating(false); }}
                  className="btn-ghost"
                  data-testid={`vehicle-edit-${v.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(v)}
                  className="btn-ghost text-red-600 hover:bg-red-50"
                  data-testid={`vehicle-delete-${v.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
